import { RoomResourceIds } from "./room-resource-state-utils";
import { isCameraShareEnabled, isScreenShareEnabled } from "./media-sharing-config-utils";

export const RoomResourceLimitKeys = Object.freeze({
  [RoomResourceIds.MICROPHONE]: "maxMicrophones",
  [RoomResourceIds.CAMERA]: "maxCameras",
  [RoomResourceIds.SCREEN_SHARE]: "maxScreenShares"
});

const RoomResourceSubscriptionLimitKeys = Object.freeze({
  [RoomResourceIds.MICROPHONE]: "subscriptionMaxMicrophones",
  [RoomResourceIds.CAMERA]: "subscriptionMaxCameras",
  [RoomResourceIds.SCREEN_SHARE]: "subscriptionMaxScreenShares"
});

const RESOURCE_POLICY = Object.freeze({
  [RoomResourceIds.MICROPHONE]: {
    enabledConfigPath: ["voiceChat", "enabled"]
  },
  [RoomResourceIds.CAMERA]: {
    isEnabled: isCameraShareEnabled
  },
  [RoomResourceIds.SCREEN_SHARE]: {
    isEnabled: isScreenShareEnabled
  }
});

function normalizeRoomResourceLimit(value) {
  const parsed = typeof value === "string" && value.trim() !== "" ? Number(value) : value;

  if (typeof parsed !== "number" || !Number.isFinite(parsed) || parsed < 0) {
    return null;
  }

  return Math.floor(parsed);
}

function mergeRoomResourceLimit(current, candidate) {
  const next = normalizeRoomResourceLimit(candidate);

  if (next === null) {
    return current;
  }

  return current === undefined ? next : Math.min(current, next);
}

function getPath(object, path) {
  return path.reduce((current, key) => current?.[key], object);
}

function isResourceEnabled(config, policy) {
  if (policy.isEnabled) {
    return policy.isEnabled(config);
  }

  return getPath(config, policy.enabledConfigPath) !== false;
}

export function resolveRoomResourceLimits({ subscriptionLimits, config, roomSettings } = {}) {
  const limits = {};

  for (const resourceId of Object.values(RoomResourceIds)) {
    const limitKey = RoomResourceLimitKeys[resourceId];
    const subscriptionLimitKey = RoomResourceSubscriptionLimitKeys[resourceId];
    const policy = RESOURCE_POLICY[resourceId];
    const candidates = [
      subscriptionLimits?.[subscriptionLimitKey],
      subscriptionLimits?.[limitKey],
      roomSettings?.resourceLimits?.[limitKey],
      roomSettings?.roomFeatureLimits?.[limitKey],
      roomSettings?.user_data?.resourceLimits?.[limitKey],
      roomSettings?.user_data?.roomFeatureLimits?.[limitKey]
    ];

    let limit;

    for (const candidate of candidates) {
      limit = mergeRoomResourceLimit(limit, candidate);
    }

    if (!isResourceEnabled(config, policy)) {
      limit = 0;
    }

    if (limit !== undefined) {
      limits[limitKey] = limit;
    }
  }

  return limits;
}
